import { AssetNetwork } from "@cpg/common";

// ─── block-explorer URL builder ────────────────────────────────────────────
//
// Maps each AssetNetwork to a public block-explorer URL, mainnet and testnet
// variants both provided so `VITE_EXPLORER_NETWORK_MODE` can switch which set
// is used at runtime without touching call sites. Kept as a partial map
// (rather than a required Record) so a network without a confidently-known
// explorer format simply falls back to `null` instead of risking a
// fabricated/broken URL — callers should render plain text + copy in that
// case. Mirrors apps/merchant/src/lib/explorers.ts.

type UrlBuilders = Partial<Record<AssetNetwork, (value: string) => string>>;

const MAINNET_TX_URL_BUILDERS: UrlBuilders = {
  [AssetNetwork.BITCOIN]: (txHash) => `https://blockstream.info/tx/${txHash}`,
  [AssetNetwork.ETHEREUM]: (txHash) => `https://etherscan.io/tx/${txHash}`,
  [AssetNetwork.BASE]: (txHash) => `https://basescan.org/tx/${txHash}`,
  [AssetNetwork.TRON]: (txHash) => `https://tronscan.org/#/transaction/${txHash}`,
  [AssetNetwork.SOLANA]: (txHash) => `https://solscan.io/tx/${txHash}`,
  [AssetNetwork.BNB]: (txHash) => `https://bscscan.com/tx/${txHash}`,
  [AssetNetwork.TON]: (txHash) => `https://tonscan.org/tx/${txHash}`,
};

const TESTNET_TX_URL_BUILDERS: UrlBuilders = {
  [AssetNetwork.BITCOIN]: (txHash) => `https://blockstream.info/testnet/tx/${txHash}`,
  [AssetNetwork.ETHEREUM]: (txHash) => `https://sepolia.etherscan.io/tx/${txHash}`,
  [AssetNetwork.BASE]: (txHash) => `https://sepolia.basescan.org/tx/${txHash}`,
  [AssetNetwork.TRON]: (txHash) => `https://shasta.tronscan.org/#/transaction/${txHash}`,
  [AssetNetwork.SOLANA]: (txHash) => `https://solscan.io/tx/${txHash}?cluster=devnet`,
  [AssetNetwork.BNB]: (txHash) => `https://testnet.bscscan.com/tx/${txHash}`,
  [AssetNetwork.TON]: (txHash) => `https://testnet.tonscan.org/tx/${txHash}`,
};

// Same explorers as above, but their address-lookup page instead of a single
// transaction — Solscan is the one builder that doesn't just swap "/tx/" for
// "/address/" (it uses "/account/").
const MAINNET_ADDRESS_URL_BUILDERS: UrlBuilders = {
  [AssetNetwork.BITCOIN]: (address) => `https://blockstream.info/address/${address}`,
  [AssetNetwork.ETHEREUM]: (address) => `https://etherscan.io/address/${address}`,
  [AssetNetwork.BASE]: (address) => `https://basescan.org/address/${address}`,
  [AssetNetwork.TRON]: (address) => `https://tronscan.org/#/address/${address}`,
  [AssetNetwork.SOLANA]: (address) => `https://solscan.io/account/${address}`,
  [AssetNetwork.BNB]: (address) => `https://bscscan.com/address/${address}`,
  [AssetNetwork.TON]: (address) => `https://tonscan.org/address/${address}`,
};

const TESTNET_ADDRESS_URL_BUILDERS: UrlBuilders = {
  [AssetNetwork.BITCOIN]: (address) => `https://blockstream.info/testnet/address/${address}`,
  [AssetNetwork.ETHEREUM]: (address) => `https://sepolia.etherscan.io/address/${address}`,
  [AssetNetwork.BASE]: (address) => `https://sepolia.basescan.org/address/${address}`,
  [AssetNetwork.TRON]: (address) => `https://shasta.tronscan.org/#/address/${address}`,
  [AssetNetwork.SOLANA]: (address) => `https://solscan.io/account/${address}?cluster=devnet`,
  [AssetNetwork.BNB]: (address) => `https://testnet.bscscan.com/address/${address}`,
  [AssetNetwork.TON]: (address) => `https://testnet.tonscan.org/address/${address}`,
};

/**
 * `VITE_EXPLORER_NETWORK_MODE=testnet` switches every explorer link in the
 * app to its testnet equivalent (Sepolia, Shasta, devnet, …). Defaults to
 * mainnet — any value other than the literal `"testnet"` is treated as
 * mainnet, so an unset/misspelled var fails safe toward the real explorers.
 */
const IS_TESTNET = import.meta.env.VITE_EXPLORER_NETWORK_MODE === "testnet";

/**
 * Builds a public block-explorer URL for `txHash` on `network`, or `null`
 * when no explorer is configured for that network (null-safe fallback: the
 * caller should render plain text + a copy button instead of a link).
 */
export function explorerTxUrl(network: AssetNetwork, txHash: string): string | null {
  const build = (IS_TESTNET ? TESTNET_TX_URL_BUILDERS : MAINNET_TX_URL_BUILDERS)[network];
  return build ? build(txHash) : null;
}

/**
 * Builds a public block-explorer URL for `address` on `network`, or `null`
 * when no explorer is configured for that network — same null-safe fallback
 * contract as `explorerTxUrl`.
 */
export function explorerAddressUrl(network: AssetNetwork, address: string): string | null {
  const build = (IS_TESTNET ? TESTNET_ADDRESS_URL_BUILDERS : MAINNET_ADDRESS_URL_BUILDERS)[network];
  return build ? build(address) : null;
}
